'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/hooks/useAuth';

export default function LandingPage() {
  const { user, loading, fetchUser } = useAuth();
  const router = useRouter();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (loading) return;
    if (user) {
      // TODO: check lastVideoId, redirect to /learn/[id] if exists
      router.replace('/catalog');
    }
  }, [user, loading, router]);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)]">
        <div className="text-sm text-[var(--color-text-secondary)]">加载中...</div>
      </div>
    );
  }

  return (
    <main className="min-h-dvh overflow-hidden bg-[linear-gradient(180deg,#f8fbff_0%,#eaf4ff_48%,#f8fbff_100%)]">
      <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col px-5 pb-28 pt-4 md:max-w-[1040px] md:px-8">
        <header className="flex shrink-0 items-center">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)] text-white shadow-[0_12px_24px_rgba(29,108,242,0.26)]">
              <LogoMark />
            </div>
            <span className="text-[17px] font-bold tracking-normal text-slate-950">轻松说</span>
          </div>
        </header>

        <section className="grid flex-1 items-center gap-5 py-5 md:grid-cols-[0.92fr_1.08fr] md:gap-12 md:py-10">
          <div className="order-1">
            <div className="max-w-[420px]">
              <h1 className="text-[32px] font-extrabold leading-[1.12] tracking-normal text-slate-950 md:text-[48px]">
                <span className="block">跟着视频，</span>
                <span className="block">把每一句英语说顺</span>
              </h1>
              <p className="mt-3 text-[14px] font-medium leading-6 text-slate-500 md:mt-4 md:text-base md:leading-7">
                看真实演讲，跟读关键句，复习收藏内容。学习节奏轻一点，开口次数多一点。
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 text-[12px] font-semibold text-slate-500 md:mt-7 md:gap-2.5">
              <SignalChip icon={<PlayIcon />}>视频精听</SignalChip>
              <SignalChip icon={<RecordIcon />}>跟读练习</SignalChip>
              <SignalChip icon={<ReviewIcon />}>句子复习</SignalChip>
            </div>
          </div>

          <div className="order-2">
            <ProductPreview />
          </div>
        </section>
      </div>

      <BottomActions />
    </main>
  );
}

function LogoMark() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 8.4v7.2L13.2 12 7 8.4Z" fill="currentColor" />
      <path
        d="M14.2 7.2c1.85 1.08 3 2.72 3 4.8s-1.15 3.72-3 4.8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function ProductPreview() {
  return (
    <div className="relative mx-auto w-full max-w-[350px] md:max-w-[440px]">
      <div className="absolute -inset-x-3 top-8 h-[76%] rounded-[34px] bg-white/50 blur-2xl" />
      <div className="relative overflow-hidden rounded-[30px] border border-white/80 bg-white/55 shadow-[0_24px_60px_rgba(37,99,235,0.16)] backdrop-blur-xl">
        <div className="relative aspect-[16/8.4] overflow-hidden rounded-b-[24px] bg-[linear-gradient(135deg,#1236d8_0%,#1d6cf2_48%,#101827_100%)] md:aspect-video md:rounded-b-[26px]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_48%_18%,rgba(255,255,255,0.28),transparent_32%),linear-gradient(90deg,rgba(0,0,0,0.28)_0%,transparent_24%,transparent_72%,rgba(0,0,0,0.35)_100%)]" />
          <div className="absolute right-6 top-0 h-full w-12 bg-black/20 shadow-[-18px_0_0_rgba(255,255,255,0.06),-36px_0_0_rgba(0,0,0,0.12)]" />
          <div className="absolute bottom-8 left-8 h-24 w-20 rounded-t-[38px] bg-[#ead7bd] shadow-[inset_0_-18px_0_rgba(15,23,42,0.18)] md:h-28 md:w-24" />
          <div className="absolute bottom-8 left-[42%] h-28 w-24 rounded-t-[46px] bg-[#f4e5d0] shadow-[inset_0_-20px_0_rgba(15,23,42,0.18)] md:h-32 md:w-28" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/24 text-white shadow-[0_14px_32px_rgba(15,23,42,0.25)] backdrop-blur-md">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-4 pb-3 pt-8 text-white">
            <div className="flex items-center justify-between text-[11px] font-semibold">
              <span className="tabular-nums">03:29 / 06:27</span>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-white/20 px-2 py-1 backdrop-blur">1x</span>
                <FullscreenIcon />
              </div>
            </div>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/35">
              <div className="h-full w-[56%] rounded-full bg-white" />
            </div>
          </div>
        </div>

        <div className="px-3 pb-4 pt-3 md:px-4 md:pb-5 md:pt-4">
          <div className="rounded-[22px] border border-[#bfd8ff] bg-[linear-gradient(135deg,#eaf3ff_0%,#f8fbff_100%)] px-3 py-3 shadow-[0_18px_34px_rgba(37,99,235,0.13)] md:rounded-[24px] md:px-4 md:py-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex min-w-[58px] items-center justify-center rounded-full border border-[#d9e8ff] bg-white/85 px-2 py-1 text-[11px] font-bold text-[var(--color-primary)]">
                <span className="mr-1 text-[9px]">▶</span>
                3:29
              </span>
              <p className="flex-1 text-[16px] font-extrabold leading-[1.5] tracking-normal text-slate-950 md:text-[18px] md:leading-[1.55]">
                is something that influences every decision you make
              </p>
            </div>
            <div className="mt-2 flex items-center gap-2 text-slate-300 md:mt-3">
              <StarIcon />
            </div>
            <div className="mt-3 rounded-[18px] border border-white/80 bg-white/88 px-3 pb-3 pt-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_26px_rgba(37,99,235,0.08)] md:mt-4">
              <div className="flex justify-center pb-1 text-[var(--color-primary)]">
                <ChevronUpIcon />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['0.5x', '0.75x', '1x'].map((speed) => (
                  <div
                    key={speed}
                    className="flex items-center justify-center gap-1 rounded-[14px] border border-slate-100 bg-slate-50 py-2 text-[13px] font-semibold text-slate-500"
                  >
                    {speed}
                    <span className="text-[9px]">▶</span>
                  </div>
                ))}
              </div>
              <div className="relative mt-3 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-slate-400">跟读练习</span>
                <div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-red-100 bg-white shadow-[0_14px_28px_rgba(255,107,102,0.2)] md:h-[52px] md:w-[52px]">
                  <span className="h-5 w-5 rounded-full bg-[#ff4f4a]" />
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-100 bg-slate-50 text-slate-300">
                  <PlayIcon />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-t-[26px] border-t border-white/80 bg-white/[0.92] px-4 pb-3 pt-3 shadow-[0_-12px_34px_rgba(15,23,42,0.07)] backdrop-blur-xl md:mt-5 md:rounded-t-[28px]">
            <div className="h-1 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-[56%] rounded-full bg-[var(--color-primary)] shadow-[0_0_12px_rgba(37,99,235,0.45)]" />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] tabular-nums text-slate-400">
              <span>3:29</span>
              <span className="font-bold text-slate-900">49 / 87</span>
              <span>6:27</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BottomActions() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-white/70 bg-white/82 px-5 pb-5 pt-3 shadow-[0_-18px_42px_rgba(15,23,42,0.09)] backdrop-blur-xl">
      <div className="mx-auto grid w-full max-w-sm grid-cols-2 gap-3">
        <Link
          href="/login"
          className="flex items-center justify-center rounded-full bg-[var(--color-primary)] px-5 py-3.5 text-base font-bold text-white shadow-[0_16px_30px_rgba(29,108,242,0.28)] transition active:scale-[0.97]"
        >
          登录
        </Link>
        <Link
          href="/register"
          className="flex items-center justify-center rounded-full border border-white/90 bg-white px-5 py-3.5 text-base font-bold text-[var(--color-primary)] shadow-[0_12px_28px_rgba(15,23,42,0.07)] transition active:scale-[0.97]"
        >
          注册
        </Link>
      </div>
    </div>
  );
}

function SignalChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white/70 px-3 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.06)] backdrop-blur">
      <span className="text-[var(--color-primary)]">{icon}</span>
      {children}
    </span>
  );
}

function PlayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function RecordIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}

function ReviewIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5 9.2 17 19 7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m12 4 2.36 4.78 5.28.77-3.82 3.72.9 5.26L12 16.05l-4.72 2.48.9-5.26-3.82-3.72 5.28-.77L12 4Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 15 12 8l7 7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4H4v4M16 4h4v4M8 20H4v-4M20 16v4h-4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  );
}
